import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";
import {
  clearGoogleHealthcareAccessToken,
  GOOGLE_HEALTHCARE_SCOPE_STRING,
  setGoogleFitOAuthClient,
} from "@/services/google/health";
import {
  GOOGLE_MOBILE_CLIENT_ID,
  GOOGLE_MOBILE_OAUTH_ERROR_EVENT,
  GOOGLE_MOBILE_OAUTH_SUCCESS_EVENT,
  GOOGLE_MOBILE_REDIRECT_URI,
  clearGoogleMobileOAuthPending,
  hasGoogleMobileOAuthPending,
  markGoogleMobileOAuthPending,
  requestGoogleMobileOAuthInNativeBrowser,
} from "@/services/google/googleMobileOAuthBridge";
import { isReactNativeWebView } from "@/utils/useWebViewBridge";

declare global {
  interface Window {
    tokenClientHealthcare: any;
    googlePopupHealthcareTimeout: NodeJS.Timeout;
  }
}

/** Codifica bytes em base64url (sem '+', '/' e '='), formato exigido pelo PKCE. */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Gera o par PKCE: um verifier aleatório e o seu challenge (SHA-256). */
async function generatePkcePair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  const verifier = base64UrlEncode(randomBytes.buffer);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  const challenge = base64UrlEncode(digest);
  return { verifier, challenge };
}

interface UseGoogleHealthcareAuthReturn {
  isConnected: boolean;
  isLoading: boolean;
  accessToken: string | null;
  handleLogin: () => void;
  handleLogout: () => Promise<void>;
}

export function useGoogleHealthcareAuth(): UseGoogleHealthcareAuthReturn {
  const { user, fetchUser } = useSessionContext();
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem("googleHealthcareAccessToken")
  );

  const googleHealthcareClientId =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID ||
    import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const googleHealthcareClientSecret =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET ||
    import.meta.env.VITE_GOOGLE_CLIENT_SECRET;
  const isConnected = Boolean(user?.refreshTokenGoogleFit);

  /**
   * Persiste tokens do Google Health no SCIM (mesmo campo do fluxo web).
   * Marca oauthClient=web para o refresh no browser usar o client certo.
   * Mobile: persistência + setGoogleFitOAuthClient("mobile") no bridge global.
   */
  const persistFitTokens = useCallback(
    async (tokens: { refresh_token?: string; access_token?: string }) => {
      if (!tokens?.refresh_token) {
        throw new Error("Refresh token Google Fit não recebido");
      }

      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
        valueField: tokens.refresh_token,
      });

      if (tokens.access_token) {
        localStorage.setItem("googleHealthcareAccessToken", tokens.access_token);
        setAccessToken(tokens.access_token);
      }

      // Refresh token gerado pelo client Web Healthcare (+ secret)
      setGoogleFitOAuthClient("web");
      localStorage.setItem("reopenFitnessModalAfterLink", "true");
      await fetchUser();
    },
    [fetchUser, user?.id]
  );

  const handleTokenResponse = useCallback(
    async (response: { code?: string }) => {
      try {
        if (!response?.code) return;

        // Alinhado ao Calendar: popup SEM redirect_uri no initCodeClient;
        // troca do code com redirect_uri = window.location.origin.
        const tokenResponse = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: googleHealthcareClientId,
            client_secret: googleHealthcareClientSecret,
            code: response.code,
            grant_type: "authorization_code",
            redirect_uri: window.location.origin,
          }),
        });

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json().catch(() => ({}));
          console.error("Erro ao trocar código Google Health:", {
            status: tokenResponse.status,
            errorData,
            clientId: googleHealthcareClientId,
            redirectUri: window.location.origin,
          });
          throw new Error(
            errorData?.error_description ||
              errorData?.error ||
              "Falha ao obter tokens Google Health"
          );
        }

        const tokens = await tokenResponse.json();
        await persistFitTokens(tokens);
        toast.success("Conta Google Health conectada com sucesso.");
      } catch (error) {
        console.error("Erro ao autenticar Google Health:", error);
        toast.error("Não foi possível conectar ao Google Health.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      persistFitTokens,
      googleHealthcareClientId,
      googleHealthcareClientSecret,
    ]
  );

  const initializeGoogleClient = useCallback(() => {
    if (!window.google) return;

    // Em ux_mode popup NÃO passar redirect_uri no initCodeClient.
    window.tokenClientHealthcare = window.google.accounts.oauth2.initCodeClient({
      client_id: googleHealthcareClientId,
      scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
      callback: (response: { code?: string }) => {
        if (window.googlePopupHealthcareTimeout) {
          clearTimeout(window.googlePopupHealthcareTimeout);
        }
        handleTokenResponse(response);
      },
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: false,
      select_account: false,
      ux_mode: "popup",
    });
  }, [googleHealthcareClientId, handleTokenResponse]);

  useEffect(() => {
    const gsiScript = document.createElement("script");
    gsiScript.src = "https://accounts.google.com/gsi/client";
    gsiScript.async = true;
    gsiScript.defer = true;
    gsiScript.onload = initializeGoogleClient;
    document.head.appendChild(gsiScript);

    const handleGoogleMessage = (event: MessageEvent) => {
      if (
        event.origin.includes("accounts.google.com") &&
        event.data &&
        (event.data.type === "cancel" || event.data.type === "close")
      ) {
        setIsLoading(false);
      }
    };

    window.addEventListener("message", handleGoogleMessage);

    return () => {
      window.removeEventListener("message", handleGoogleMessage);
      gsiScript.remove();
    };
  }, [initializeGoogleClient]);

  /** Sincroniza loading com o listener global do bridge (retorno OAuth mobile). */
  useEffect(() => {
    const onSuccess = (event: Event) => {
      const detail = (event as CustomEvent).detail as { flow?: string };
      if (detail?.flow !== "healthcare") return;
      setIsLoading(false);
      setAccessToken(localStorage.getItem("googleHealthcareAccessToken"));
    };

    const onError = (event: Event) => {
      const detail = (event as CustomEvent).detail as { flow?: string };
      if (detail?.flow && detail.flow !== "healthcare") return;
      if (hasGoogleMobileOAuthPending()) return;
      setIsLoading(false);
    };

    window.addEventListener(GOOGLE_MOBILE_OAUTH_SUCCESS_EVENT, onSuccess);
    window.addEventListener(GOOGLE_MOBILE_OAUTH_ERROR_EVENT, onError);

    return () => {
      window.removeEventListener(GOOGLE_MOBILE_OAUTH_SUCCESS_EVENT, onSuccess);
      window.removeEventListener(GOOGLE_MOBILE_OAUTH_ERROR_EVENT, onError);
    };
  }, []);

  const handleLogin = useCallback(async () => {
    // Mobile: OAuth no navegador do sistema; tokens via googleMobileOAuthBridge.
    if (isReactNativeWebView()) {
      try {
        setIsLoading(true);

        const { verifier, challenge } = await generatePkcePair();
        markGoogleMobileOAuthPending("healthcare", verifier);

        const authUrl =
          "https://accounts.google.com/o/oauth2/v2/auth?" +
          new URLSearchParams({
            client_id: GOOGLE_MOBILE_CLIENT_ID,
            redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
            response_type: "code",
            access_type: "offline",
            prompt: "consent",
            code_challenge: challenge,
            code_challenge_method: "S256",
            scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
          }).toString();

        requestGoogleMobileOAuthInNativeBrowser({
          url: authUrl,
          redirectUri: GOOGLE_MOBILE_REDIRECT_URI,
          flow: "healthcare",
        });
      } catch (error) {
        console.error("Erro ao iniciar login Google Health (mobile):", error);
        clearGoogleMobileOAuthPending();
        setIsLoading(false);
        toast.error("Não foi possível abrir o login Google.");
      }
      return;
    }

    if (!window.tokenClientHealthcare) return;

    setIsLoading(true);
    window.googlePopupHealthcareTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 60000);
    window.tokenClientHealthcare.requestCode();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
        valueField: "",
      });
      clearGoogleHealthcareAccessToken();
      setAccessToken(null);
      await fetchUser();
    } catch (error) {
      console.error("Erro ao desconectar Google Health:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser, user?.id]);

  return {
    isConnected,
    isLoading,
    accessToken,
    handleLogin,
    handleLogout,
  };
}
